import { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Check, ChevronsUpDown, Loader2, Lock, X } from "lucide-react";
import { PppMember } from "@/types";
import { useDebounce } from "@/hooks/useDebounce";
import { useUsers } from "@/hooks/useUsers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { PppFormValues } from "./ppp-form-schema";

interface MemberPickerProps {
  value: string[];
  onChange: (pids: string[]) => void;
  /** Already-whitelisted members, so their chips render before any search resolves */
  initialMembers?: PppMember[];
  disabled?: boolean;
}

/** Searchable multi-user picker for the confidentiality whitelist (server search, 300ms debounce). */
function PppMemberPicker({ value, onChange, initialMembers, disabled }: MemberPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<PppMember[]>([]);
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading } = useUsers(debouncedSearch, 1, 20);
  const users = useMemo(() => data?.data ?? [], [data?.data]);

  // Names for the chips: the stored members, whoever was picked this session, plus the current page.
  const known = useMemo(() => {
    const map = new Map<string, PppMember>();
    [...(initialMembers ?? []), ...picked].forEach((m) => map.set(m.pid, m));
    users.forEach((u) => map.set(u.pid, { pid: u.pid, fullName: u.fullName, avatarUrl: u.avatarUrl }));
    return map;
  }, [initialMembers, picked, users]);

  const toggle = (member: PppMember) => {
    setPicked((prev) => (prev.some((p) => p.pid === member.pid) ? prev : [...prev, member]));
    onChange(value.includes(member.pid) ? value.filter((p) => p !== member.pid) : [...value, member.pid]);
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
            className="w-full justify-between font-normal">
            <span className={cn("truncate", value.length === 0 && "text-muted-foreground")}>
              {value.length === 0 ? "Select members…" : `${value.length} member${value.length === 1 ? "" : "s"}`}
            </span>
            {isLoading ? <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" /> : <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search people…" value={search} onValueChange={setSearch} />
            <CommandList>
              <CommandEmpty>{isLoading ? "Loading…" : "No users found."}</CommandEmpty>
              <CommandGroup>
                {users.map((user) => (
                  <CommandItem key={user.pid} value={user.pid}
                    onSelect={() => toggle({ pid: user.pid, fullName: user.fullName, avatarUrl: user.avatarUrl })}>
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value.includes(user.pid) ? "opacity-100" : "opacity-0")} />
                    <UserAvatar src={user.avatarUrl} name={user.fullName} className="mr-2 h-6 w-6 shrink-0" fallbackClassName="text-[10px]" />
                    <span className="truncate">{user.fullName}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((pid) => {
            const member = known.get(pid);
            return (
              <Badge key={pid} variant="secondary" className="gap-1 py-0.5 pl-0.5 pr-1 font-normal">
                <UserAvatar src={member?.avatarUrl ?? undefined} name={member?.fullName} className="h-5 w-5" fallbackClassName="text-[9px]" />
                <span className="max-w-[160px] truncate text-xs">{member?.fullName ?? "Member"}</span>
                <button type="button" aria-label={`Remove ${member?.fullName ?? "member"}`} disabled={disabled}
                  onClick={() => onChange(value.filter((p) => p !== pid))}
                  className="rounded-full p-0.5 hover:bg-muted-foreground/20">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface PppConfidentialFieldsProps {
  form: UseFormReturn<PppFormValues>;
  disabled?: boolean;
  initialMembers?: PppMember[];
}

/** Leader-only pair: the confidentiality flag and the member whitelist that may see the PPP. */
export function PppConfidentialFields({ form, disabled, initialMembers }: PppConfidentialFieldsProps) {
  const isConfidential = form.watch("isConfidential");

  return (
    <>
      <FormField control={form.control} name="isConfidential" render={({ field }) => (
        <FormItem>
          <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div>
                <FormLabel className="cursor-pointer text-sm font-medium leading-none">Confidential</FormLabel>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Only leaders, directors and the members below can see this PPP and its deliverables.
                </p>
              </div>
            </div>
            <FormControl>
              <Switch checked={!!field.value} onCheckedChange={field.onChange} disabled={disabled} />
            </FormControl>
          </div>
          <FormMessage />
        </FormItem>
      )} />

      {isConfidential && (
        <FormField control={form.control} name="memberPids" render={({ field }) => (
          <FormItem>
            <FormLabel>Members</FormLabel>
            <PppMemberPicker value={field.value} onChange={field.onChange} initialMembers={initialMembers} disabled={disabled} />
            <FormMessage />
          </FormItem>
        )} />
      )}
    </>
  );
}
